import React, { useState, useEffect } from 'react';
import { FileText, Edit2, Eye, DollarSign, Users } from 'lucide-react';
import api from '@/api/api';
import { useReportPagination } from '@/components/ReportPagination';

const FeeSettingReport = () => {
  const [feeSettings, setFeeSettings] = useState([]);
  const { paginatedData: pageFeeSettings, PaginationControls } = useReportPagination(feeSettings);
  const [loading, setLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedSetting, setSelectedSetting] = useState(null);
  const [editData, setEditData] = useState({
    discount_amount: 0
  });
  const [stats, setStats] = useState({
    totalStudents: 0,
    totalFees: 0,
    totalPaid: 0,
    totalPending: 0
  });

  useEffect(() => {
    fetchFeeSettings();
  }, []);

  const fetchFeeSettings = async () => {
    setLoading(true);
    try {
      const response = await api.get('/course-fees/fee-settings');
      const settingsData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      setFeeSettings(settingsData);
      
      // Calculate stats
      const totalStudents = settingsData.length;
      const totalFees = settingsData.reduce((sum, setting) => sum + (setting.total_fee || 0), 0);
      const totalPaid = settingsData.reduce((sum, setting) => sum + (setting.total_paid_amount || 0), 0);
      const totalPending = settingsData.reduce((sum, setting) => sum + (setting.pending_amount || 0), 0);
      
      setStats({ totalStudents, totalFees, totalPaid, totalPending });
    } catch (error) {
      console.error('Error fetching fee settings:', error);
      alert(error.response?.data?.detail || 'Failed to fetch fee settings');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (setting) => {
    setSelectedSetting(setting);
    setEditData({
      discount_amount: setting.discount_amount || 0
    });
    setShowEditModal(true);
  };

  const handleUpdate = async () => {
    if (!selectedSetting) return;

    setLoading(true);
    try {
      await api.put(`/course-fees/fee-setting/${selectedSetting.id}`, editData);
      alert('Fee setting updated successfully!');
      setShowEditModal(false);
      fetchFeeSettings();
    } catch (error) {
      console.error('Error updating fee setting:', error);
      alert(error.response?.data?.detail || 'Failed to update fee setting');
    } finally {
      setLoading(false);
    }
  };

  const getPaymentStatusBadge = (paid, total) => {
    if (paid === total) return 'bg-green-100 text-green-800';
    if (paid > 0) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const getPaymentStatusText = (paid, total) => {
    if (paid === total) return 'Fully Paid';
    if (paid > 0) return 'Partial';
    return 'Pending';
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Fee Setting Report</h1>
        <p className="text-sm text-gray-500 mt-1">View and manage all student fee settings</p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Students</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalStudents}</p>
            </div>
            <Users className="h-8 w-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Fees</p>
              <p className="text-2xl font-bold text-gray-900">₹{stats.totalFees.toLocaleString('en-IN')}</p>
            </div>
            <DollarSign className="h-8 w-8 text-purple-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Paid</p>
              <p className="text-2xl font-bold text-green-600">₹{stats.totalPaid.toLocaleString('en-IN')}</p>
            </div>
            <DollarSign className="h-8 w-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Pending</p>
              <p className="text-2xl font-bold text-orange-600">₹{stats.totalPending.toLocaleString('en-IN')}</p>
            </div>
            <DollarSign className="h-8 w-8 text-orange-600" />
          </div>
        </div>
      </div>

      {/* Fee Settings Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Fee Settings
          </h2>
          <button
            onClick={fetchFeeSettings}
            disabled={loading}
            className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:bg-gray-400"
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Admission No</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Course</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Fee</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Discount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Balance</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Installments</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading && feeSettings.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-6 py-8 text-center text-gray-500">
                    Loading fee settings...
                  </td>
                </tr>
              ) : feeSettings.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-6 py-8 text-center text-gray-500">
                    No fee settings found
                  </td>
                </tr>
              ) : (
                pageFeeSettings.map((setting) => (
                  <tr key={setting.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {setting.student_name}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {setting.admission_number}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {setting.course_name}
                    </td>
                    <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                      ₹{setting.total_fee.toLocaleString('en-IN')}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      ₹{(setting.discount_amount || 0).toLocaleString('en-IN')}
                    </td>
                    <td className="px-6 py-4 text-sm font-semibold text-blue-600">
                      ₹{setting.balance_amount.toLocaleString('en-IN')}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {setting.paid_installments || 0} / {setting.total_installments || 0}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getPaymentStatusBadge(setting.paid_installments, setting.total_installments)}`}>
                        {getPaymentStatusText(setting.paid_installments, setting.total_installments)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditClick(setting)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                        >
                          <Edit2 size={14} />
                          Edit
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <PaginationControls />
      </div>

      {/* Edit Modal */}
      {showEditModal && selectedSetting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <Edit2 className="h-8 w-8 text-blue-600" />
              <div>
                <h2 className="text-xl font-bold text-gray-900">Edit Fee Setting</h2>
                <p className="text-sm text-gray-500">{selectedSetting.student_name} - {selectedSetting.admission_number}</p>
              </div>
            </div>

            <div className="space-y-4">
              {/* Fee Breakdown */}
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Total Fee:</span>
                  <span className="font-semibold">₹{selectedSetting.total_fee.toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Booking Amount:</span>
                  <span className="font-semibold">₹{(selectedSetting.booking_amount || 0).toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Admission Amount:</span>
                  <span className="font-semibold">₹{(selectedSetting.admission_amount || 0).toLocaleString('en-IN')}</span>
                </div>
                <div className="border-t pt-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Current Discount:</span>
                    <span className="font-semibold text-orange-600">₹{(selectedSetting.discount_amount || 0).toLocaleString('en-IN')}</span>
                  </div>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-900 font-medium">Balance Amount:</span>
                  <span className="font-bold text-blue-600">₹{selectedSetting.balance_amount.toLocaleString('en-IN')}</span>
                </div>
              </div>

              {/* Edit Discount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Update Discount Amount
                </label>
                <input
                  type="number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Enter new discount amount"
                  value={editData.discount_amount}
                  onChange={(e) => setEditData({ ...editData, discount_amount: parseFloat(e.target.value) || 0 })}
                />
                <p className="text-xs text-gray-500 mt-1">
                  New Balance: ₹{(selectedSetting.total_fee - (selectedSetting.booking_amount || 0) - (selectedSetting.admission_amount || 0) - editData.discount_amount).toLocaleString('en-IN')}
                </p>
              </div>

              {/* Installment Info */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-900 font-medium mb-2">Installment Update</p>
                <p className="text-xs text-blue-700">
                  Total Installments: {selectedSetting.total_installments || 0} | 
                  Paid: {selectedSetting.paid_installments || 0} | 
                  Pending: {selectedSetting.pending_installments || 0}
                </p>
                <p className="text-xs text-blue-700 mt-1">
                  ⚠️ Note: Only pending installments will be recalculated with the new balance.
                </p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowEditModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
              >
                {loading ? 'Updating...' : 'Update Fee Setting'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeeSettingReport;
